import React, { useState, useEffect, useMemo } from 'react';
import { ArrowLeft, Search, Eye, ChevronDown, ChevronRight, Phone, MapPin, Star, Award, SlidersHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { DietIcon } from '@/components/ui/DietIcon';
import { MenuItemImage } from '@/components/ui/MenuItemImage';
import { RestaurantLogo } from '@/components/ui/RestaurantLogo';
import { ImagePreviewModal } from '@/components/ui/ImagePreviewModal';
import { MenuSearch } from './MenuSearch';
import { FilterPanel } from './FilterPanel';
import ImageMenuPreview from './ImageMenuPreview';
import { SPICE_LEVELS, ALLERGEN_TYPES, CUISINE_TYPES } from '@/types';
import type { MenuSection, MenuItem, Restaurant } from '@/types';
import type { MenuFilterState, SortOptions } from '@/lib/utils/searchUtils';
import { 
  createEmptyFilterState, 
  createDefaultSortOptions, 
  searchAndFilter, 
  countActiveFilters 
} from '@/lib/utils/searchUtils';

interface MenuPreviewProps {
  restaurant: Restaurant;
  sections: MenuSection[];
  items: MenuItem[];
  onBack: () => void;
  showPoweredBy?: boolean; // New optional prop for public display
  isPublicView?: boolean; // Flag to distinguish public vs preview usage
  onViewTracked?: (analytics: { restaurantId: string; viewId: string }) => void; // Analytics callback
}

/**
 * MenuPreview - Basic preview of the menu structure
 * Shows how the menu will look to customers
 */
export const MenuPreview: React.FC<MenuPreviewProps> = ({
  restaurant,
  sections,
  items,
  onBack,
  showPoweredBy = false,
  // isPublicView = false, // Future use
  // onViewTracked, // Future use
}) => {
  // State to manage which sections are expanded (all collapsed by default)
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [previewImage, setPreviewImage] = useState<{ url: string; name: string } | null>(null);
  
  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<MenuFilterState>(createEmptyFilterState());
  const [sortOptions, setSortOptions] = useState<SortOptions>(createDefaultSortOptions());
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);

  const handleFiltersChange = (newFilters: Partial<MenuFilterState>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };
  const filteredItems = useMemo(() => {
    const result = searchAndFilter(items, sections, {
      searchQuery,
      filters,
      sort: sortOptions
    });
    return result.items;
  }, [items, sections, searchQuery, filters, sortOptions]);

  // Expand all sections when searching
  useEffect(() => {
    if (searchQuery.length > 0) {
      // Get all section IDs that have filtered items
      const sectionsWithItems = new Set(
        filteredItems.map(item => item.sectionId)
      );
      setExpandedSections(sectionsWithItems);
    }
  }, [searchQuery, filteredItems]);

  const toggleSection = (sectionId: string) => {
    const newExpandedSections = new Set(expandedSections);
    if (newExpandedSections.has(sectionId)) {
      newExpandedSections.delete(sectionId);
    } else {
      newExpandedSections.add(sectionId);
    }
    setExpandedSections(newExpandedSections);
  };

  const handleImageClick = (imageUrl: string, itemName: string) => {
    setPreviewImage({ url: imageUrl, name: itemName });
  };

  const closeImagePreview = () => {
    setPreviewImage(null);
  };

  const getItemsForSection = (sectionId: string) => {
    return filteredItems.filter(item => item.sectionId === sectionId && item.isAvailable);
  };

  // Check if there are any available items across all sections
  const hasAvailableItems = useMemo(() => {
    return items.some(item => item.isAvailable);
  }, [items]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header - Only show for non-public views */}
      {!showPoweredBy && (
        <header className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-14 sm:h-16">
              <div className="flex items-center space-x-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onBack}
                  leftIcon={<ArrowLeft className="w-4 h-4" />}
                  className="p-2"
                  title="Back to Menu Builder"
                />
                
                <div>
                  <h1 className="text-lg sm:text-xl font-semibold text-gray-900">
                    Menu Preview
                  </h1>
                  <p className="text-sm text-gray-600 hidden sm:block">
                    How customers will see your menu
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <Eye className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-600">Customer View</span>
              </div>
            </div>
          </div>
        </header>
      )}

      {/* Customer Menu View */}
      <main className="max-w-md mx-auto bg-white min-h-screen">
        {/* Restaurant Header */}
        <div className="bg-white p-6 border-b">
          <div className="flex items-center space-x-4">
            {/* Restaurant Logo with Loading Animation */}
            <RestaurantLogo
              src={restaurant.logoUrl}
              alt={`${restaurant.name} logo`}
              className="w-16 h-16 flex-shrink-0 rounded-lg"
            />
            
            {/* Restaurant Name and Description */}
            <div className="flex-1">
              <h1 
                className="text-2xl font-bold mb-2" 
                style={{ color: 'var(--theme-color)' }}
              >
                {restaurant.name}
              </h1>
              {/* Restaurant Description - Left aligned below name */}
              {restaurant.description && (
                <p className="text-gray-600 text-sm leading-relaxed">
                  {restaurant.description}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Contact Info */}
        {(restaurant.phone || restaurant.address) && (
          <div className="p-4 bg-gray-50 border-b text-sm text-gray-600">
            {restaurant.phone && (
              <div className="flex items-center space-x-2 mb-1">
                <Phone className="w-4 h-4 text-gray-500" />
                <a 
                  href={`tel:${restaurant.phone}`}
                  className="transition-colors hover:opacity-80"
                  style={{ color: 'var(--theme-color)' }}
                >
                  {restaurant.phone}
                </a>
              </div>
            )}
            {restaurant.address && (
              <div className="flex items-center space-x-2">
                <MapPin className="w-4 h-4 text-gray-500" />
                <button
                  onClick={() => {
                    const encodedAddress = encodeURIComponent(restaurant.address!);
                    // Always use Google Maps for consistency
                    const mapsUrl = `https://maps.google.com/maps?q=${encodedAddress}`;
                    window.open(mapsUrl, '_blank');
                  }}
                  className="text-left transition-colors hover:opacity-80"
                  style={{ color: 'var(--theme-color)' }}
                >
                  {restaurant.address}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Image Menu Preview - Above Search */}
        <ImageMenuPreview
          restaurantId={restaurant.id}
          hasDigitalMenu={sections.length > 0}
          hasAvailableDigitalItems={hasAvailableItems}
        />

        {/* Search and Filter Controls - Only show if there are sections AND available items */}
        {sections.length > 0 && hasAvailableItems && (
          <div className="p-4 border-b bg-white space-y-4">
            {/* Search Bar */}
            <div className="flex items-center space-x-2">
              <div className="flex-1">
                <MenuSearch
                  value={searchQuery}
                  onChange={setSearchQuery}
                  placeholder="Search menu items..."
                />
              </div>
              
              {/* Filter Button */}
              <Button
                variant={countActiveFilters(filters) > 0 ? 'primary' : 'ghost'}
                size="md"
                onClick={() => setIsFilterPanelOpen(true)}
                className="relative p-3"
                title="Filters"
              >
                <SlidersHorizontal className="w-5 h-5" />
                {countActiveFilters(filters) > 0 && (
                  <span className="absolute -top-1 -right-1 bg-apple-red text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {countActiveFilters(filters)}
                  </span>
                )}
              </Button>
            </div>

            {/* Search Results Summary */}
            {searchQuery && (
              <div className="text-sm text-gray-600">
                {filteredItems.length} item{filteredItems.length !== 1 ? 's' : ''} found for "{searchQuery}"
              </div>
            )}
          </div>
        )}

        {/* Menu Sections - Only show if sections exist */}
        {sections.length > 0 && (
          <div className="p-4">
            {(() => {
              // Filter sections that have items matching the search/filter criteria
              const sectionsWithItems = sections
                .filter(section => section.isVisible)
                .map(section => ({
                  section,
                  items: getItemsForSection(section.id)
                }))
                .filter(({ items }) => items.length > 0);

              if (sectionsWithItems.length === 0 && (searchQuery || countActiveFilters(filters) > 0)) {
                return (
                  <div className="text-center py-12 text-gray-500">
                    <Search className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p className="text-lg font-medium mb-2">No items found</p>
                    <p className="text-sm mb-4">
                      Try adjusting your search or filters
                    </p>
                    <div className="space-x-2">
                      {searchQuery && (
                        <Button variant="ghost" size="sm" onClick={() => setSearchQuery('')}>
                          Clear search
                        </Button>
                      )}
                      {countActiveFilters(filters) > 0 && (
                        <Button variant="ghost" size="sm" onClick={() => setFilters(createEmptyFilterState())}>
                          Clear filters
                        </Button>
                      )}
                    </div>
                  </div>
                );
              }

              return sectionsWithItems.map(({ section, items: sectionItems }) => {
                const isExpanded = expandedSections.has(section.id);
                
                return (
                  <div key={section.id} className="mb-6">
                    {/* Collapsible Section Header */}
                    <button
                      onClick={() => toggleSection(section.id)}
                      className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${
                        isExpanded 
                          ? 'text-white' 
                          : 'bg-gray-50 hover:bg-gray-100'
                      }`}
                      style={isExpanded ? { backgroundColor: 'var(--theme-color)' } : {}}
                    >
                      <div className="text-left">
                        <h2 className={`text-lg font-bold ${isExpanded ? 'text-white' : 'text-gray-900'}`}>
                          {section.name}
                        </h2>
                        {section.description && (
                          <p className={`text-sm mt-1 ${isExpanded ? 'text-white opacity-90' : 'text-gray-600'}`}>
                            {section.description}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        {searchQuery ? (
                          <span className={`text-xs ${isExpanded ? 'text-white opacity-75' : 'text-gray-500'}`}>
                            {sectionItems.length} matching
                          </span>
                        ) : (
                          <span className={`text-xs ${isExpanded ? 'text-white opacity-75' : 'text-gray-500'}`}>
                            {sectionItems.length} item{sectionItems.length !== 1 ? 's' : ''}
                          </span>
                        )}
                        {isExpanded ? (
                          <ChevronDown className="w-5 h-5 text-white opacity-75" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                    </button>
                    
                    {/* Collapsible Section Content */}
                    {isExpanded && (
                      <div className="mt-4">
                        {sectionItems.length === 0 ? (
                          <p className="text-sm text-gray-500 italic py-4">
                            No items in this section yet
                          </p>
                        ) : (
                          <div className="space-y-4">
                            {sectionItems.map((item) => {
                              // Check for enhanced allergen data
                              const hasEnhancedAllergens = item.enhancedAllergens && (
                                Object.keys(ALLERGEN_TYPES).some(key => 
                                  item.enhancedAllergens![key as keyof typeof ALLERGEN_TYPES]
                                ) || 
                                item.enhancedAllergens.custom.length > 0 ||
                                item.enhancedAllergens.mayContain.length > 0
                              );

                              // Use enhanced spice level if available, otherwise map old numeric system
                              // Only show spice info if there's a meaningful spice level (not 'none' or undefined)
                              const spiceInfo = (() => {
                                // Check enhanced spice level first
                                if (item.enhancedSpiceLevel?.level && 
                                    item.enhancedSpiceLevel.level !== 'none' && 
                                    item.enhancedSpiceLevel.level.trim() !== '') {
                                  return SPICE_LEVELS[item.enhancedSpiceLevel.level];
                                }
                                
                                // Fallback to old numeric system
                                if (item.spiceLevel && item.spiceLevel > 0) {
                                  const spiceLevelMap = {
                                    1: 'mild',
                                    2: 'mild', 
                                    3: 'medium',
                                    4: 'hot',
                                    5: 'extra-hot'
                                  } as const;
                                  return SPICE_LEVELS[spiceLevelMap[item.spiceLevel as keyof typeof spiceLevelMap]];
                                }
                                
                                return null;
                              })();

                              return (
                                <div 
                                  key={item.id} 
                                  className="p-3 bg-white rounded-lg border border-gray-100 space-y-3"
                                >
                                  {/* Header with image and basic info */}
                                  <div className="flex items-start space-x-3">
                                    {item.imageUrl ? (
                                      <MenuItemImage
                                        src={item.imageUrl}
                                        alt={item.name}
                                        className="w-16 h-16 rounded-lg flex-shrink-0"
                                        onClick={() => handleImageClick(item.imageUrl!, item.name)}
                                      />
                                    ) : (
                                      <div className="w-16 h-16 flex-shrink-0 bg-gray-100 rounded-lg flex items-center justify-center">
                                        <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                                                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                      </div>
                                    )}
                                    {/* Item Details - match image height and center content */}
                                    <div className="flex-1 h-16 flex flex-col justify-center">
                                      <div className="flex items-center justify-between">
                                        <div className="flex items-center space-x-2 flex-1">
                                          <h3 
                                            className="font-medium" 
                                            style={{ color: 'var(--theme-color)' }}
                                          >
                                            {item.name}
                                          </h3>
                                          {/* Diet type indicator next to name */}
                                          <DietIcon 
                                            type={item.dietType === 'contains-eggs' ? 'eggs' : item.dietType} 
                                            size={16}
                                          />
                                        </div>
                                        <span 
                                          className="font-semibold ml-4 flex-shrink-0"
                                          style={{ color: 'var(--theme-color)' }}
                                        >
                                          {item.price}
                                        </span>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Description */}
                                  {item.description && (
                                    <p className="text-sm text-gray-600">{item.description}</p>
                                  )}

                                  {/* Additional info (prep time, calories, spice) */}
                                  {(item.preparationTime || item.calories || spiceInfo) && (
                                    <div className="flex items-center space-x-4 text-xs text-gray-500">
                                      {item.preparationTime && (
                                        <span>{item.preparationTime} min</span>
                                      )}
                                      {item.calories && (
                                        <span>{item.calories} kcal</span>
                                      )}
                                      {spiceInfo && (
                                        <span>Spice: {spiceInfo.label}</span>
                                      )}
                                    </div>
                                  )}

                                  {/* Enhanced spice preparation notes */}
                                  {item.enhancedSpiceLevel?.preparationNotes && (
                                    <div className="text-xs text-gray-500">
                                      Note: {item.enhancedSpiceLevel.preparationNotes}
                                    </div>
                                  )}

                                  {/* Cuisine Tags and Special Badges */}
                                  {(item.cuisineTags && item.cuisineTags.length > 0) || item.isSignatureDish || item.isChefSpecial ? (
                                    <div className="flex flex-wrap gap-1">
                                      {/* Cuisine Tags */}
                                      {item.cuisineTags && item.cuisineTags.map((tag, index) => {
                                        const displayText = [
                                          CUISINE_TYPES[tag.type].label,
                                          tag.region && tag.region.charAt(0).toUpperCase() + tag.region.slice(1),
                                          tag.subType && tag.subType.split('-').map(word => 
                                            word.charAt(0).toUpperCase() + word.slice(1)
                                          ).join(' ')
                                        ].filter(Boolean).join(' - ');
                                        
                                        return (
                                          <span
                                            key={index}
                                            className="px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary"
                                          >
                                            {displayText}
                                          </span>
                                        );
                                      })}
                                      
                                      {/* Special badges */}
                                      {item.isSignatureDish && (
                                        <div className="flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                          <Star className="w-3 h-3" />
                                          <span>Signature</span>
                                        </div>
                                      )}
                                      {item.isChefSpecial && (
                                        <div className="flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                          <Award className="w-3 h-3" />
                                          <span>Chef's Special</span>
                                        </div>
                                      )}
                                    </div>
                                  ) : null}

                                  {/* Ingredients */}
                                  {item.ingredients && item.ingredients.length > 0 && (
                                    <div className="text-xs text-gray-600">
                                      <span className="font-medium">Ingredients: </span>
                                      <span>{item.ingredients.join(', ')}</span>
                                    </div>
                                  )}

                                  {/* Enhanced Allergen Information */}
                                  {hasEnhancedAllergens && item.enhancedAllergens && (
                                    <div className="text-xs">
                                      <div>
                                        <span className="font-medium text-gray-700">Contains: </span>
                                        <span className="text-red-600">
                                          {[
                                            ...Object.entries(ALLERGEN_TYPES)
                                              .filter(([key]) => item.enhancedAllergens![key as keyof typeof ALLERGEN_TYPES])
                                              .map(([, value]) => value.label),
                                            ...item.enhancedAllergens.custom
                                          ].join(', ')}
                                        </span>
                                      </div>
                                      {item.enhancedAllergens.mayContain.length > 0 && (
                                        <div className="mt-1">
                                          <span className="font-medium text-gray-700">May contain: </span>
                                          <span className="text-yellow-600">
                                            {item.enhancedAllergens.mayContain.join(', ')}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  )}

                                  {/* Fallback to basic allergens if no enhanced data */}
                                  {!hasEnhancedAllergens && item.allergens && item.allergens.length > 0 && (
                                    <div className="text-xs">
                                      <span className="font-medium text-gray-700">Allergens: </span>
                                      <span className="text-red-600">
                                        {item.allergens.join(', ')}
                                      </span>
                                    </div>
                                  )}

                                  {/* Can be prepared section */}
                                  {(item.isVegan || item.isJain) && (
                                    <div className="flex items-center gap-3 text-xs text-gray-600 mt-2">
                                      {item.isVegan && (
                                        <div className="flex items-center gap-1">
                                          <DietIcon type="vegan" size={14} />
                                          <span>Can be prepared vegan</span>
                                        </div>
                                      )}
                                      {item.isJain && (
                                        <div className="flex items-center gap-1">
                                          <DietIcon type="jain" size={14} />
                                          <span>Can be prepared Jain</span>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              });
            })()}
          </div>
        )}
      </main>

      {/* Powered by Footer for Public View */}
      {showPoweredBy && (
        <footer className="bg-white border-t border-gray-100 py-4">
          <div className="max-w-md mx-auto text-center">
            <p className="text-xs text-gray-500">
              Powered by{' '}
              <a 
                href="https://menu.qodrix.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary hover:text-primary/80 font-medium transition-colors"
              >
                Qodrix Menu
              </a>
            </p>
          </div>
        </footer>
      )}

      {/* Filter Panel */}
      {isFilterPanelOpen && (
        <FilterPanel
          isOpen={isFilterPanelOpen}
          onClose={() => setIsFilterPanelOpen(false)}
          filters={filters}
          onFiltersChange={handleFiltersChange}
          sort={sortOptions}
          onSortChange={setSortOptions}
          items={items}
          sections={sections}
        />
      )}

      {/* Image Preview Modal */}
      {previewImage && (
        <ImagePreviewModal
          isOpen={!!previewImage}
          onClose={closeImagePreview}
          imageUrl={previewImage.url}
          itemName={previewImage.name}
        />
      )}
    </div>
  );
};